import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";

import { ChangeSortButton } from "./ChangeSortButton";
import { FilterSelect } from "./FilterSelect";
import { useProjectsLocations } from "./hooks";
import { OnboardingStatus, ProjectData } from "../../../models/ProjectData";
import Pagination from "../../Pagination";
import ScoreItem from "../../ScoreItem";

import "./styles.scss";

export interface IProjectFacilitiesListProps {
  project: ProjectData;
  onOpenProjectLocationDetailsModal: (projectLocationId: string) => void;
}

export const ProjectFacilitiesList: React.FunctionComponent<
  IProjectFacilitiesListProps
> = (props) => {
  const { project, onOpenProjectLocationDetailsModal } = props;
  const { t: _t } = useTranslation();

  const t = (key: string, params?: { [key: string]: string | number }) =>
    _t(`projectManagementPage.projectFacilitiesList.${key}`, params);

  const getStatusDisplay = (status?: OnboardingStatus) => {
    if (!status) return null;

    const statusConfig = {
      COMPLETE: { text: t("status_complete"), className: "status-complete" },
      INCOMPLETE: {
        text: t("status_incomplete"),
        className: "status-incomplete",
      },
      SCHEDULED: { text: t("status_scheduled"), className: "status-scheduled" },
    };

    const config = statusConfig[status];
    return (
      <span className={`status-badge ${config.className}`}>{config.text}</span>
    );
  };

  const {
    query,
    filterFormData,
    handleChangeFilterFormData,
    searchParams,
    setSearchParams,
  } = useProjectsLocations(project?.organization_id, project?.id.toString());

  const projectsLocations = query.data?.data;

  const locationCount = project?.total_facility_count || 0;
  const targetFacilityCount = project?.target_facility_count || 0;
  const locationCountText = targetFacilityCount
    ? `${locationCount} / ${targetFacilityCount}`
    : locationCount;

  // Calculate onboarding complete count
  const onboardingCompleteCount =
    projectsLocations?.filter((item) => item.onboarding_status === "COMPLETE")
      .length || 0;
  const onboardingCompleteText = targetFacilityCount
    ? `${onboardingCompleteCount} / ${targetFacilityCount}`
    : onboardingCompleteCount;

  return (
    <div className="white-border-panel project-facilities-list">
      <div className="project-facilities-list-header">
        <h2 className="project-facilities-list-title">
          {t("facilities_list")}
        </h2>
        <div className="header-stats">
          <span className="registered-facility-count">
            {t("registered_facility_count", {
              locationCount: locationCountText,
            })}
          </span>
          <span className="onboarding-complete-count">
            {t("onboarding_complete", { count: onboardingCompleteText })}
          </span>
        </div>
      </div>
      <FilterSelect
        searchParams={searchParams}
        setSearchParams={setSearchParams}
      />
      <table className="project-facilities-list-content">
        <thead>
          <tr>
            <th className="facility-name">
              <ChangeSortButton
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                fieldName="location.name"
                translationKey="facility_name"
              />
            </th>
            <th className="registered-at">
              <ChangeSortButton
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                fieldName="project_join_date"
                translationKey="registered_at"
              />
            </th>
            <th className="health-index">
              <ChangeSortButton
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                fieldName="location.location_stats.score"
                translationKey="health_index"
              />
            </th>
            <th className="status">
              <ChangeSortButton
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                fieldName="onboarding_status"
                translationKey="status"
              />
            </th>
            <th className="actions"></th>
          </tr>
        </thead>
        <tbody>
          {projectsLocations &&
            projectsLocations.map((item) => (
              <tr key={item.id}>
                <td className="facility-name">{item.location.name}</td>
                <td className="registered-at">
                  {dayjs(item.project_join_date).format("YYYY/MM/DD")}
                </td>
                <td className="health-index">
                  <ScoreItem
                    score={item.location.location_stats?.score || "n"}
                  />
                </td>
                <td className="status">
                  {getStatusDisplay(item.onboarding_status)}
                </td>
                <td className="actions">
                  <button
                    className="btn btn-border project-facilities-list-details-button"
                    onClick={() =>
                      onOpenProjectLocationDetailsModal(item.id.toString())
                    }
                  >
                    {t("table.row.details")}
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <Pagination
        filterFormData={filterFormData}
        totalRowsNumber={query.data?.count || 0}
        onChangeFilterFormData={handleChangeFilterFormData}
      />
    </div>
  );
};
